import './FindAccountForm.css';
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useSetRecoilState } from 'recoil';
import { navigationBarState } from '../../../states/NavigationBar';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function FindAccountForm() {
    const navigate = useNavigate();
    const setNavigationBarState = useSetRecoilState(navigationBarState);

    // Tab state: 'id' or 'password'
    const [activeTab, setActiveTab] = useState('id');

    // Common states
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationSent, setVerificationSent] = useState(false);
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [timer, setTimer] = useState(0);
    const [resendTimer, setResendTimer] = useState(0);

    // Password reset states
    const [username, setUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

    // Result states
    const [foundUsername, setFoundUsername] = useState('');
    const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

    // Loading states
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Refs
    const codeRef = useRef(null);
    const usernameRef = useRef(null);
    const newPasswordRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setNavigationBarState({
            title: 'ID/PW 찾기',
            'left-item': { type: 'back' },
        });
    }, [setNavigationBarState]);

    // Timer for verification code (3분 유효기간)
    useEffect(() => {
        let interval = null;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(t => t - 1);
            }, 1000);
        } else if (timer === 0 && verificationSent && !phoneVerified) {
            setVerificationSent(false);
        }
        return () => clearInterval(interval);
    }, [timer, verificationSent, phoneVerified]);

    // Timer for resend cooldown (1분 대기)
    useEffect(() => {
        let interval = null;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(t => t - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    // Reset states when tab changes
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPhoneNumber('');
        setVerificationCode('');
        setVerificationSent(false);
        setPhoneVerified(false);
        setTimer(0);
        setResendTimer(0);
        setUsername('');
        setNewPassword('');
        setNewPasswordConfirm('');
        setFoundUsername('');
        setPasswordResetSuccess(false);
    };

    // Format timer
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Format phone number
    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/[^\d]/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setPhoneNumber(formatted);
        setPhoneVerified(false);
        setVerificationSent(false);
        setResendTimer(0);
        setFoundUsername('');
        setPasswordResetSuccess(false);
    };

    // Send verification code
    const sendVerificationCode = async () => {
        const phone = phoneNumber.replace(/[^\d]/g, '');
        if (phone.length < 10) {
            toast.error('올바른 휴대폰 번호를 입력해주세요.');
            return;
        }

        if (resendTimer > 0) {
            toast.error(`${resendTimer}초 후에 다시 시도해주세요.`);
            return;
        }

        setSendingCode(true);
        try {
            const response = await axios.post('/users/send-verification/', {
                phone_number: phone
            });
            if (response.data.success) {
                toast.success('인증번호가 발송되었습니다.');
                setVerificationSent(true);
                setTimer(180);
                setResendTimer(60);
                setVerificationCode('');
                codeRef.current?.focus();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '인증번호 발송에 실패했습니다.');
        } finally {
            setSendingCode(false);
        }
    };

    // Verify code
    const verifyCode = async () => {
        if (verificationCode.length !== 6) {
            toast.error('6자리 인증번호를 입력해주세요.');
            return;
        }

        const phone = phoneNumber.replace(/[^\d]/g, '');
        setVerifyingCode(true);
        try {
            const response = await axios.post('/users/verify-code/', {
                phone_number: phone,
                verification_code: verificationCode
            });
            if (response.data.success) {
                toast.success('인증이 완료되었습니다.');
                setPhoneVerified(true);
                setTimer(0);
                if (activeTab === 'password') {
                    usernameRef.current?.focus();
                } else if (activeTab === 'id') {
                    // 아이디 찾기 탭에서는 자동으로 아이디 조회
                    try {
                        const findResponse = await axios.post('/users/find-username/', {
                            phone_number: phone
                        });
                        if (findResponse.data.success) {
                            setFoundUsername(findResponse.data.username);
                        } else {
                            toast.error(findResponse.data.message);
                        }
                    } catch (findError) {
                        toast.error(findError.response?.data?.message || '아이디 찾기에 실패했습니다.');
                    }
                }
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '인증에 실패했습니다.');
        } finally {
            setVerifyingCode(false);
        }
    };

    // Find username
    const findUsername = async () => {
        if (!phoneVerified) {
            toast.error('휴대폰 인증을 완료해주세요.');
            return;
        }

        const phone = phoneNumber.replace(/[^\d]/g, '');
        setSubmitting(true);
        try {
            const response = await axios.post('/users/find-username/', {
                phone_number: phone
            });
            if (response.data.success) {
                setFoundUsername(response.data.username);
                toast.success('아이디를 찾았습니다.');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '아이디 찾기에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset password
    const resetPassword = async () => {
        if (!phoneVerified) {
            toast.error('휴대폰 인증을 완료해주세요.');
            return;
        }

        if (!username.trim()) {
            toast.error('아이디를 입력해주세요.');
            return;
        }

        if (newPassword.length < 4) {
            toast.error('비밀번호는 4자 이상이어야 합니다.');
            return;
        }

        if (newPassword !== newPasswordConfirm) {
            toast.error('비밀번호가 일치하지 않습니다.');
            return;
        }

        const phone = phoneNumber.replace(/[^\d]/g, '');
        setSubmitting(true);
        try {
            const response = await axios.post('/users/reset-password/', {
                phone_number: phone,
                username: username.trim(),
                new_password: newPassword
            });
            if (response.data.success) {
                setPasswordResetSuccess(true);
                toast.success('비밀번호가 변경되었습니다.');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '비밀번호 변경에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    const goToLogin = () => {
        navigate('/');
    };

    // Password validation
    const passwordsMatch = newPassword.length > 0 && newPassword === newPasswordConfirm;
    const passwordValid = newPassword.length >= 4;

    // Form validation
    const canFindUsername = phoneVerified && !foundUsername;
    const canResetPassword = phoneVerified && username.trim() && passwordValid && passwordsMatch && !passwordResetSuccess;

    return (
        <div className="find-account-page">
            <div className="find-account-container">
                {/* Tabs */}
            <div className="find-account-tabs">
                <button
                    className={`find-account-tab ${activeTab === 'id' ? 'active' : ''}`}
                    onClick={() => handleTabChange('id')}
                >
                    아이디 찾기
                </button>
                <button
                    className={`find-account-tab ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => handleTabChange('password')}
                >
                    비밀번호 재설정
                </button>
            </div>

            <div className="find-account-form">
                {/* Description */}
                <div className="find-account-description">
                    {activeTab === 'id'
                        ? '회원가입 시 등록한 휴대폰 번호로 인증하여 아이디를 찾을 수 있습니다.'
                        : '회원가입 시 등록한 휴대폰 번호로 인증하여 비밀번호를 재설정할 수 있습니다.'}
                </div>

                {/* Phone Number */}
                <div className="find-account-section">
                    <div className="find-account-input-group">
                        <label className="find-account-input-label">휴대폰 번호</label>
                        <div className="find-account-input-wrapper">
                            <input
                                type="tel"
                                className={`find-account-input with-button ${phoneVerified ? 'success' : ''}`}
                                placeholder="010-0000-0000"
                                value={phoneNumber}
                                onChange={handlePhoneChange}
                                disabled={phoneVerified}
                                maxLength={13}
                            />
                            {!phoneVerified && (
                                <button
                                    className={`find-account-input-button ${verificationSent ? 'outlined' : ''}`}
                                    onClick={sendVerificationCode}
                                    disabled={phoneNumber.replace(/[^\d]/g, '').length < 10 || sendingCode || resendTimer > 0}
                                >
                                    {sendingCode ? '발송중...' :
                                     resendTimer > 0 ? `${resendTimer}초` :
                                     (verificationSent ? '재발송' : '인증요청')}
                                </button>
                            )}
                            {phoneVerified && (
                                <div className="find-account-verified-badge">
                                    <CheckCircleIcon style={{ fontSize: 14 }} />
                                    인증완료
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Verification Code */}
                    {verificationSent && !phoneVerified && (
                        <div className="find-account-input-group">
                            <label className="find-account-input-label">인증번호</label>
                            <div className="find-account-verification-row">
                                <div className="find-account-input-wrapper find-account-verification-input">
                                    <input
                                        ref={codeRef}
                                        type="text"
                                        className="find-account-input"
                                        placeholder="6자리 숫자 입력"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                                        maxLength={6}
                                    />
                                </div>
                                <div className="find-account-verification-timer">{formatTimer(timer)}</div>
                                <button
                                    className="find-account-verification-button"
                                    onClick={verifyCode}
                                    disabled={verificationCode.length !== 6 || verifyingCode}
                                >
                                    {verifyingCode ? '...' : '확인'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ID Find Result */}
                {activeTab === 'id' && foundUsername && (
                    <div className="find-account-result">
                        <div className="find-account-result-label">회원님의 아이디</div>
                        <div className="find-account-result-value-wrapper">
                            <div className="find-account-result-value">{foundUsername}</div>
                            <button
                                className="find-account-copy-button"
                                onClick={() => {
                                    navigator.clipboard.writeText(foundUsername);
                                    toast.success('아이디가 복사되었습니다.');
                                }}
                            >
                                복사
                            </button>
                        </div>
                    </div>
                )}

                {/* Password Reset: Username Input */}
                {activeTab === 'password' && phoneVerified && !passwordResetSuccess && (
                    <div className="find-account-section">
                        <div className="find-account-input-group">
                            <label className="find-account-input-label">아이디</label>
                            <div className="find-account-input-wrapper">
                                <input
                                    ref={usernameRef}
                                    type="text"
                                    className="find-account-input"
                                    placeholder="아이디 입력"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="find-account-input-group">
                            <label className="find-account-input-label">새 비밀번호</label>
                            <div className="find-account-input-wrapper">
                                <input
                                    ref={newPasswordRef}
                                    type="password"
                                    className={`find-account-input ${newPassword.length > 0 ? (passwordValid ? 'success' : 'error') : ''}`}
                                    placeholder="4자 이상 입력"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </div>
                            {newPassword.length > 0 && !passwordValid && (
                                <div className="find-account-input-error">비밀번호는 4자 이상이어야 합니다.</div>
                            )}
                        </div>

                        <div className="find-account-input-group">
                            <label className="find-account-input-label">새 비밀번호 확인</label>
                            <div className="find-account-input-wrapper">
                                <input
                                    type="password"
                                    className={`find-account-input ${newPasswordConfirm.length > 0 ? (passwordsMatch ? 'success' : 'error') : ''}`}
                                    placeholder="비밀번호 재입력"
                                    value={newPasswordConfirm}
                                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                                />
                            </div>
                            {newPasswordConfirm.length > 0 && !passwordsMatch && (
                                <div className="find-account-input-error">비밀번호가 일치하지 않습니다.</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Password Reset Success */}
                {activeTab === 'password' && passwordResetSuccess && (
                    <div className="find-account-result">
                        <div className="find-account-result-badge">
                            <CheckCircleIcon style={{ fontSize: 16 }} />
                            비밀번호 변경 완료
                        </div>
                        <div className="find-account-result-hint">새 비밀번호로 로그인해주세요.</div>
                    </div>
                )}

                {/* Submit Button */}
                {activeTab === 'password' && !passwordResetSuccess && (
                    <button
                        className="find-account-submit-button"
                        onClick={resetPassword}
                        disabled={!canResetPassword || submitting}
                    >
                        {submitting ? '변경 중...' : '비밀번호 변경'}
                    </button>
                )}

                {/* Go to Login Button */}
                {(foundUsername || passwordResetSuccess) && (
                    <button
                        className="find-account-submit-button"
                        onClick={goToLogin}
                    >
                        로그인하기
                    </button>
                )}

                {/* Login Link */}
                <div className="find-account-login-link">
                    <a onClick={goToLogin}>로그인으로 돌아가기</a>
                </div>
            </div>
            </div>
        </div>
    );
}

export default FindAccountForm;
